import type { ReactNode } from 'react';
import { useState } from 'react';
import { Share2, MessageCircle, Copy, Navigation } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  buildShareText,
  canNativeShare,
  copyShareText,
  nativeShare,
  openDirections,
  openWhatsApp,
  type SharePayload,
} from '@/utils/share';

interface ShareSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while the caller is still hydrating — pair with `loading`. */
  payload: SharePayload | null;
  loading?: boolean;
  error?: string | null;
  /** Extra actions rendered under the button row (e.g. "Remove pin"). */
  footer?: ReactNode;
  /**
   * Caller-owned inputs that change what gets sent, rendered above the
   * preview so the text visibly updates as they are used (e.g. the wildfire
   * "enroute" toggles). Hidden while the payload is loading or errored —
   * there is nothing to alter yet.
   */
  controls?: ReactNode;
  /**
   * Show the message that will be sent. Off for a dropped pin, where the
   * text is just a coordinate pair and the buttons are the whole point.
   */
  showPreview?: boolean;
}

/**
 * One share surface for every record in the app — a dropped pin, an FPA
 * member, a water source.
 *
 * The two-step shape (open the sheet, *then* tap a share button) is not a
 * styling preference. `navigator.share` requires transient user activation,
 * and callers such as the members list must await a fetch before they can
 * even build the text — awaiting first spends the activation and iOS Safari
 * throws NotAllowedError. Hydrating while the sheet shows a skeleton, and
 * letting the button press be the gesture, is the only arrangement that
 * works on every platform.
 */
export function ShareSheet({
  open,
  onOpenChange,
  payload,
  loading = false,
  error = null,
  footer,
  controls,
  showPreview = true,
}: ShareSheetProps) {
  const [busy, setBusy] = useState(false);
  const text = payload ? buildShareText(payload) : '';
  const ready = Boolean(payload) && !loading && !error;

  async function handleNativeShare() {
    if (!payload || busy) return;
    setBusy(true);
    try {
      const outcome = await nativeShare(payload);
      if (outcome === 'shared') onOpenChange(false);
      else if (outcome === 'failed') toast.error('Could not open the share sheet.');
      // 'cancelled' is the user backing out — say nothing.
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!payload || busy) return;
    setBusy(true);
    try {
      const outcome = await copyShareText(payload);
      if (outcome === 'copied') {
        toast.success('Copied to clipboard');
        onOpenChange(false);
      } else {
        toast.error('Could not copy to clipboard.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl px-4 pb-8">
        <SheetHeader className="mb-3">
          <SheetTitle className="truncate text-base">
            {payload?.title ?? 'Share'}
          </SheetTitle>
          {payload?.kind && (
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {payload.kind}
            </p>
          )}
          <SheetDescription className="sr-only">
            Share these details, or open directions to this location.
          </SheetDescription>
        </SheetHeader>

        {controls && ready && <div className="mb-3">{controls}</div>}

        {error ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : loading || !payload ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : showPreview ? (
          <pre className="max-h-56 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs leading-relaxed">
            {text}
          </pre>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {canNativeShare() && (
            <Button size="sm" onClick={handleNativeShare} disabled={!ready || busy} className="gap-1.5">
              <Share2 className="h-3.5 w-3.5" />
              Share…
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => payload && openWhatsApp(text)}
            disabled={!ready || busy}
            className="gap-1.5"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopy}
            disabled={!ready || busy}
            className="gap-1.5"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy
          </Button>
          {payload?.location && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                openDirections(
                  payload.location!.lat,
                  payload.location!.lng,
                  payload.location!.label ?? payload.title,
                )
              }
              disabled={busy}
              className="gap-1.5"
            >
              <Navigation className="h-3.5 w-3.5" />
              Directions
            </Button>
          )}
          {footer}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ShareSheet;
